import { useContext, useMemo } from "react";
import Slider from "react-slick";
import { config } from "../config";
import { ProductContext } from "../context/ProductsContext";
import { useNavigate } from "react-router-dom";

const Category = ({ toggleSidebar }) => {
  const { products, categories, filterProductsByCategory } = useContext(ProductContext);

  // শুধুমাত্র main categories নেওয়া (যাদের parent_id null)
  const mainCategories = useMemo(() => {
    return categories.filter(category => 
      category.parent_id === null
    );
  }, [categories]);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: Math.min(mainCategories.length, 4),
    slidesToScroll: 1,
    rows: 2,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      { 
        breakpoint: 1024, 
        settings: { 
          slidesToShow: Math.min(mainCategories.length, 4),
          rows: 2 
        } 
      },
      { 
        breakpoint: 768, 
        settings: { 
          slidesToShow: Math.min(mainCategories.length, 4),
          rows: 2 
        } 
      },
      { 
        breakpoint: 640, 
        settings: { 
          slidesToShow: Math.min(mainCategories.length, 4),
          rows: 2 
        } 
      },
      { 
        breakpoint: 480, 
        settings: { 
          slidesToShow: Math.min(mainCategories.length, 4),
          rows: 2 
        } 
      }
    ]
  };

  const imageUrl = config.imageUrl;

  const navigate = useNavigate();

  const handleCategoryClick = (categoryId) => {
    navigate(`/category/${categoryId}`);
    if (window.innerWidth < 1024 && toggleSidebar) {
      toggleSidebar();
    }
  };

  // If there are no main categories, don't render the component
  if (mainCategories.length === 0) {
    return null;
  }

  // Adjust rows based on category count
  const totalSlides = Math.min(mainCategories.length, 8);
  const actualRows = totalSlides <= 4 ? 1 : 2;

  const finalSettings = {
    ...settings,
    rows: actualRows,
    slidesToShow: Math.min(mainCategories.length, 4),
    responsive: settings.responsive.map(resp => ({
      ...resp,
      settings: {
        ...resp.settings,
        rows: mainCategories.length <= 4 ? 1 : 2
      }
    }))
  };

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 text-center">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-800">Shop by Category</h2>
        <p className="text-gray-600 mt-2 text-sm lg:text-base">
          Discover products from our wide range of categories
        </p>
      </div>
      
      <Slider {...finalSettings}>
        {mainCategories.map((category) => {
          // Count products in this category and all its subcategories
          const getAllCategoryIds = (catId) => {
            const categoryIds = [catId];
            // Find all subcategories of this category
            const subCategories = categories.filter(cat => cat.parent_id === catId);
            subCategories.forEach(subCat => {
              categoryIds.push(subCat.id);
            });
            return categoryIds;
          };

          const categoryIds = getAllCategoryIds(category.id);
          const productCount = products.filter((product) => 
            categoryIds.includes(product.category_id)
          ).length;
          
          return (
            <div key={category.id} className="px-[2px] md:px-2">
              <div className="flex flex-col h-full">
                <button
                  onClick={() => handleCategoryClick(category.id)}
                  className="w-full text-left group relative flex flex-col h-full"
                >
                  {/* Main Image Container - Square Shape */}
                  <div className="relative flex-shrink-0 overflow-hidden rounded-xl lg:rounded-2xl bg-white shadow-sm border border-gray-200 transition-all duration-300 hover:shadow-lg hover:scale-105 p-1 aspect-square">
                    <div className="relative z-10 rounded-lg lg:rounded-xl overflow-hidden bg-white w-full h-full">
                      <img
                        src={`${imageUrl}/${category.image}`}
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.target.src = '/images/category-placeholder.png';
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Category Name Below Image - Fixed Height Container */}
                  <div className="mt-2 flex-grow min-h-[40px] flex items-center justify-center">
                    <p className="font-semibold text-xs sm:text-sm md:text-base text-gray-800 text-center leading-tight px-1">
                      {category.name}
                    </p>
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </Slider>
    </div>
  );
};

export default Category;